import * as React from "react"
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu"
import { cx } from "@/lib/utils/cx"

// Radix dropdown wrapped in our design vocabulary — rounded-2xl popover,
// soft shadow, emerald hover, destructive variant for delete-style items.
// Mirrors DesignSelect / DesignTooltip; pick this one for ⋯ overflow menus
// on cards and table rows. The shadcn `dropdown-menu.tsx` is left in place
// for legacy callers but new code should import DesignMenu instead.
//
// Usage:
//
//   <DesignMenu.Root>
//     <DesignMenu.Trigger>⋯</DesignMenu.Trigger>
//     <DesignMenu.Content align="end">
//       <DesignMenu.Item onSelect={...}>Resend invite</DesignMenu.Item>
//       <DesignMenu.Separator />
//       <DesignMenu.Item destructive onSelect={...}>Revoke</DesignMenu.Item>
//     </DesignMenu.Content>
//   </DesignMenu.Root>

const Root = DropdownMenuPrimitive.Root
const Trigger = DropdownMenuPrimitive.Trigger
const Group = DropdownMenuPrimitive.Group
const Portal = DropdownMenuPrimitive.Portal

const Content = React.forwardRef<
  React.ComponentRef<typeof DropdownMenuPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content>
>(({ className, sideOffset = 6, ...props }, ref) => (
  <DropdownMenuPrimitive.Portal>
    <DropdownMenuPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cx(
        "z-50 min-w-[180px] rounded-2xl border border-zinc-200/70 bg-white shadow-2xl shadow-zinc-900/15 overflow-hidden p-1.5",
        "data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
        "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
        "data-[side=bottom]:slide-in-from-top-1 data-[side=top]:slide-in-from-bottom-1",
        className,
      )}
      {...props}
    />
  </DropdownMenuPrimitive.Portal>
))
Content.displayName = "DesignMenu.Content"

interface ItemProps
  extends React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item> {
  destructive?: boolean
  icon?: React.ReactNode
}

const Item = React.forwardRef<
  React.ComponentRef<typeof DropdownMenuPrimitive.Item>,
  ItemProps
>(({ className, destructive, icon, children, ...props }, ref) => (
  <DropdownMenuPrimitive.Item
    ref={ref}
    className={cx(
      "relative flex w-full items-center gap-2.5 px-2.5 py-2 rounded-xl text-[13px] font-medium cursor-pointer outline-none select-none",
      destructive
        ? "text-red-600 data-[highlighted]:bg-red-50"
        : "text-zinc-700 data-[highlighted]:bg-zinc-50 data-[highlighted]:text-zinc-900",
      "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      className,
    )}
    {...props}
  >
    {icon && <span className="shrink-0 [&>svg]:size-4">{icon}</span>}
    <span className="flex-1 truncate text-left">{children}</span>
  </DropdownMenuPrimitive.Item>
))
Item.displayName = "DesignMenu.Item"

const Separator = React.forwardRef<
  React.ComponentRef<typeof DropdownMenuPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Separator
    ref={ref}
    className={cx("-mx-1 my-1 h-px bg-zinc-100", className)}
    {...props}
  />
))
Separator.displayName = "DesignMenu.Separator"

const Label = React.forwardRef<
  React.ComponentRef<typeof DropdownMenuPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Label>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Label
    ref={ref}
    className={cx(
      "px-2.5 pt-2 pb-1 text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-400",
      className,
    )}
    {...props}
  />
))
Label.displayName = "DesignMenu.Label"

export const DesignMenu = {
  Root,
  Trigger,
  Group,
  Portal,
  Content,
  Item,
  Separator,
  Label,
}
